import type { PaymentInfo } from '@festina/moneyin-general-types/moneyin'
import { notEmpty } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import { Card, CardBody, CardHeader } from '@festina-life/ui/structure'
import { useMemo, useState } from 'react'

import { IdMapperProvider } from '../../../context/IdMapperProvider'
import { isMatchable } from '../../../hooks/usePaymentInfoTableColumns'
import { useComponent, useHook } from '../../../lenteHooks'
import { MatchPaymentFiltersForm } from './MatchPaymentFiltersForm'

type Props = {
  selectedPaymentInfos: PaymentInfo[]
  onSelectedPaymentInfosChanged: (selectedPaymentInfos: PaymentInfo[]) => void
}

export const MatchPaymentCard = ({
  selectedPaymentInfos,
  onSelectedPaymentInfosChanged,
}: Props) => {
  const PaymentInfoTable = useComponent('pages.moneyIn.PaymentInfoTable')

  const translate = useTranslate()

  const [isShowingFilters, setIsShowingFilters] = useState(true)
  const { data, error, mutateAsync, reset } = useHook(
    'lazyFetchers.moneyIn.useLazyFetchPayments',
  )()

  const { payments, ownerIds, hasMoreResults } = useMemo(() => {
    const payments = data?.paymentInfos?.filter(notEmpty) ?? []
    const ownerIds = payments.map(({ ownerId }) => ownerId).filter(notEmpty)
    return { payments, ownerIds, hasMoreResults: data?.hasMoreResults }
  }, [data?.paymentInfos, data?.hasMoreResults])

  const handlePaymentSelectionToggle = (payment: PaymentInfo) => {
    if (!isMatchable(payment.paymentStatus)) {
      return
    }

    const isAlreadySelected = selectedPaymentInfos.some(
      ({ paymentId }) => paymentId === payment.paymentId,
    )

    const nextSelectedPaymentInfos = isAlreadySelected
      ? selectedPaymentInfos.filter(
          ({ paymentId }) => paymentId !== payment.paymentId,
        )
      : [...selectedPaymentInfos, payment]

    onSelectedPaymentInfosChanged(nextSelectedPaymentInfos)
  }

  return (
    <Card>
      <CardHeader
        title={translate('moneyIn.match.title')}
        actions={[
          {
            key: 'match.payments.filters',
            iconName: 'Filter',
            onClick: () => setIsShowingFilters(!isShowingFilters),
          },
        ]}
      />
      <MatchPaymentFiltersForm
        error={error}
        onSearch={(values) => {
          onSelectedPaymentInfosChanged([])
          return mutateAsync(values)
        }}
        isHidden={!isShowingFilters}
        onReset={() => {
          reset()
          onSelectedPaymentInfosChanged([])
        }}
      />
      <CardBody outerClassName="min-w-0 grow">
        <IdMapperProvider ids={ownerIds}>
          <PaymentInfoTable
            payments={payments}
            hasMoreResults={hasMoreResults}
            paymentsSelectedForAttach={selectedPaymentInfos}
            onPaymentSelectedForAttach={handlePaymentSelectionToggle}
          />
        </IdMapperProvider>
      </CardBody>
    </Card>
  )
}
